import { FormControl, FormHelperText, Grid, InputAdornment, MenuItem, TextField, Box } from '@vapor/material'
import { Select } from '@mui/material'
import { useCallback, useMemo, useState, useEffect } from 'react'
import * as R from 'ramda'
import FormItem from '../form-fields/form-item/form-item'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons'

export type Contact<T> = {
  contact: string
  type?: T
}

export type ContactInputError = {
  contact?: { message?: string }
  type?: { message?: string }
}

export type ContactInputProps<T> = {
  defaultValue?: Contact<T>
  value?: Contact<T>
  onChange?: (value: Contact<T>) => void
  types?: T[]
  getTypeId: (type: T) => string
  displayType: (type: T) => string
  label?: string
  error?: ContactInputError
  disabled?: boolean
}

function ContactInput<T>(props: ContactInputProps<T>) {
  const { defaultValue, value, onChange, getTypeId, displayType, error, disabled, label } = props
  const types = useMemo(() => {
    return R.uniqBy(getTypeId, props.types ?? [])
  }, [props.types, getTypeId])

  const [localValue, setLocalValue] = useState<Contact<T>>(defaultValue ? getLocalValue(defaultValue, types, getTypeId) : getLocalValue({ contact: '' }, types, getTypeId))

  useEffect(() => {
    if (value) {
      const newLocalValue = getLocalValue(value, types, getTypeId)
      if (JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        setLocalValue(newLocalValue)
        onChange?.(newLocalValue)
      }
    }
  }, [value])

  const onLocalChange = useCallback(
    (newLocalValue: Contact<T>) => {
      if (!value && JSON.stringify(newLocalValue) !== JSON.stringify(localValue)) {
        setLocalValue(newLocalValue)
        onChange?.(newLocalValue)
      }
      if (value && JSON.stringify(newLocalValue) !== JSON.stringify(value)) {
        setLocalValue(newLocalValue)
        onChange?.(newLocalValue)
      }
    },
    [value, onChange, localValue],
  )

  const typeValue = useMemo(() => {
    return localValue.type ? getTypeId(localValue.type) : ''
  }, [localValue.type, getTypeId])

  const onTypeChange = useCallback(
    (typeId: string) => {
      const filteredTypes = types?.filter((type) => getTypeId(type) === typeId).slice(0, 1)
      const newContact = getLocalValue(localValue, filteredTypes, getTypeId)
      onLocalChange(newContact)
    },
    [types, getTypeId, localValue, onLocalChange],
  )

  const onContactChange = useCallback(
    (text: string) => {
      const newContact = { contact: text, type: localValue.type }
      onLocalChange(newContact)
    },
    [localValue, onLocalChange],
  )

  const handleClearType = useCallback(() => {
    onLocalChange({ contact: '' })
  }, [onLocalChange])

  return (
    <FormItem //
      error={error?.contact !== undefined}
      fieldLabel={label}
      fullWidth
    >
      <Grid //
        container
        spacing={1}
      >
        {types && types.length > 1 && (
          <Grid
            item
            xs={3}
            //style={{ display: 'flex', alignItems: 'flex-end' }}
          >
            <FormControl fullWidth>
              <Select //
                error={error?.type !== undefined}
                value={typeValue}
                onChange={(event) => onTypeChange(event.target.value)}
                fullWidth
                size="small"
                disabled={disabled}
              >
                {types
                  .sort((a, b) => (displayType(a) < displayType(b) ? -1 : 1))
                  .map((type) => (
                    <MenuItem key={getTypeId(type)} value={getTypeId(type)}>
                      {displayType(type)}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText error={error?.type !== undefined}>{error?.type?.message}</FormHelperText>
            </FormControl>
          </Grid>
        )}

        <Grid
          item
          xs={types && types.length > 1 ? 9 : 12}
          // style={{ display: 'flex', alignItems: 'flex-end' }}
        >
          <TextField //
            fullWidth
            value={localValue.contact}
            onChange={(event) => onContactChange(event.target.value)}
            error={error?.contact !== undefined}
            disabled={disabled}
            size="small"
            helperText={error?.contact?.message}
            sx={{
              '& .MuiInputAdornment-root': {
                cursor: 'pointer',
              },
              '& .MuiInputAdornment-root:hover .clear-icon': {
                color: 'gray !important',
              },
              '& .MuiInputAdornment-root .clear-icon': {
                color: '#ccc !important',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ visibility: typeValue ? 'visible' : 'hidden', eventIcon: 'auto' }} onClick={handleClearType}>
                  <Box p={0.5}>
                    <FontAwesomeIcon className="clear-icon" icon={faCircleXmark} size={'sm'} />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </FormItem>
  )
}

export default ContactInput

function getLocalValue<T>(value: Contact<T>, types: T[] | undefined, getTypeId: (type: T) => string): Contact<T> {
  if (!types) {
    return { contact: value.contact }
  }
  if (types.length === 1) {
    return { contact: value.contact, type: types[0] }
  }
  const valueType = value.type
  if (valueType) {
    const localType = types.find((type) => getTypeId(type) === getTypeId(valueType))
    return { contact: value.contact, type: localType }
  }
  return { contact: value.contact }
}
